import React, { useState } from "react";
import classnames from "classnames";
import { useQueryParam } from "common/hooks/useQueryParams";
import { useResetPasswordMutation } from "common/types/mainHooks";
import { Banner } from "@jsq/ds/core";
import { type UnauthenticatedArenaObjectType } from "apps/portal/Portal/common/types";
import {
  Form,
  SubmitButton,
  createPasswordValidationSchema,
  FormCreatePasswordInput,
} from "@jsq/ds/forms";

import styles from "../common/styles.scss";
import { defaultSingleManagerPoweredByMessage } from "../common/LoginCard/constants";
import { LoginCard } from "../common/LoginCard";

const UNKNOWN_ERROR_MESSAGE = "An unexpected error occurred, please try again.";
const shouldDisplayPoweredByHeader = window.location.href.includes("app.");

type Props = {
  onSuccess?: () => void;
  arena?: UnauthenticatedArenaObjectType;
};

type ResetPasswordFormik = {
  password: string;
};

export const ResetPassword: React.FC<Props> = (props) => {
  const CREATE_PASSWORD_INPUT_FIELD_NAME = "password";
  const CONFIRM_PASSWORD_INPUT_FIELD_NAME = "confirmPassword";
  const passwordValidationSchema = createPasswordValidationSchema({
    isRequired: true,
    passwordName: CREATE_PASSWORD_INPUT_FIELD_NAME,
    confirmPasswordName: CONFIRM_PASSWORD_INPUT_FIELD_NAME,
  });

  const [resetPassword] = useResetPasswordMutation();
  const [code] = useQueryParam("code");

  const [error, setError] = useState("");

  const handleSubmit = async (values: ResetPasswordFormik) => {
    setError("");

    try {
      const { data } = await resetPassword({
        variables: {
          resetPasswordInput: {
            token: code,
            password: values.password,
            confirmPassword: values.password,
          },
        },
        context: { unauthenticated: true },
      });

      if (!data?.resetPassword?.result) {
        setError(UNKNOWN_ERROR_MESSAGE);
        return;
      }

      switch (data.resetPassword.result.__typename) {
        case "ResetPasswordResponse":
          props.onSuccess?.();
          break;
        case "MutationErrorResponse":
          setError(data.resetPassword.result.message);
          break;
        default:
          setError(UNKNOWN_ERROR_MESSAGE);
      }
    } catch (e) {
      setError(UNKNOWN_ERROR_MESSAGE);
    }
  };

  return (
    <LoginCard
      showPoweredByHeader={shouldDisplayPoweredByHeader}
      poweredByHelpMessage={defaultSingleManagerPoweredByMessage(props.arena?.name)}
      showCardTitle
      cardTitle="Set new password"
    >
      {error && (
        <Banner
          variant="danger"
          title="Error"
          description={error}
          className={styles.Login__error}
        />
      )}
      <div className={styles.Login__form}>
        {/* @ts-expect-error: onSubmit should be expecting Partial<{[CREATE_PASSWORD_INPUT_FIELD_NAME]:string, [CONFIRM_PASSWORD_INPUT_FIELD_NAME]: string}> */}
        <Form
          initialValues={{
            [CREATE_PASSWORD_INPUT_FIELD_NAME]: "",
            [CONFIRM_PASSWORD_INPUT_FIELD_NAME]: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={passwordValidationSchema}
        >
          <FormCreatePasswordInput
            className="stacked-md"
            passwordName={CREATE_PASSWORD_INPUT_FIELD_NAME}
            confirmPasswordName={CONFIRM_PASSWORD_INPUT_FIELD_NAME}
            placeholder=""
            isFullWidth
          />
          <SubmitButton
            isFullWidth
            className={classnames("inline-sm", styles.Login__submitButton)}
            data-cy="reset-password-and-sign-in"
            variant={
              props?.arena?.signInButtonBackgroundColor || props?.arena?.signInButtonTextColor
                ? "custom"
                : "primary"
            }
            backgroundColor={props?.arena?.signInButtonBackgroundColor || undefined}
            textColor={props?.arena?.signInButtonTextColor || undefined}
          >
            Reset password
          </SubmitButton>
        </Form>
      </div>
    </LoginCard>
  );
};
